import { ReactNode, useState, useEffect } from "react";
import { useCardDensity, DensityContext } from "@/hooks/use-card-density";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useOrganization } from "@/hooks/use-organization";
import { useStoreContext } from "@/hooks/use-store-context";
import { useTheme } from "@/hooks/use-theme";
import { useDualRole, setPortalChoice } from "@/hooks/use-dual-role";
import { useInactivityTimer } from "@/hooks/use-inactivity-timer";
import { InactivityWarning } from "@/components/InactivityWarning";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  LayoutDashboard,
  Package,
  Store,
  ShoppingCart,
  Truck,
  Users,
  Menu as MenuIcon,
  X,
  LogOut,
  ChevronRight,
  ChevronDown,
  Eye,
  EyeOff,
  ClipboardList,
  Sun,
  Moon,
  BarChart2,
  CreditCard,
  Trash2,
  History,
  UserCircle,
  ArrowRightLeft,
  PackageCheck,
  Settings,
  Clock,
  CheckSquare,
  UtensilsCrossed,
  HeartPulse,
  Building2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { GlobalSearch } from "@/components/GlobalSearch";
import { NotificationBell } from "@/components/NotificationBell";
import { differenceInDays, parseISO } from "date-fns";
const vrgLogo = "/vrg-logo.svg";

type AppRole = "owner" | "manager" | "staff" | "viewer";

type NavItem = {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href: string;
  roles: string[];
};

type NavGroup = {
  label: string;
  items: NavItem[];
};

const TOP_LEVEL_ITEMS: NavItem[] = [
  { label: "Dashboard", icon: LayoutDashboard, href: "/dashboard", roles: ["owner", "manager"] },
  { label: "Inventory", icon: ClipboardList, href: "/dashboard/inventory", roles: ["owner", "manager", "staff", "viewer"] },
  { label: "Catalog",   icon: Package,       href: "/dashboard/products",  roles: ["owner"] },
  { label: "Vendors",   icon: Truck,         href: "/dashboard/vendors",   roles: ["owner", "manager"] },
];

const NAV_GROUPS: NavGroup[] = [
  {
    label: "Operations",
    items: [
      { label: "Order Guide",   icon: ShoppingCart,   href: "/dashboard/reorder",    roles: ["owner", "manager"] },
      { label: "Order History", icon: History,        href: "/dashboard/orders",     roles: ["owner", "manager"] },
      { label: "Deliveries",    icon: PackageCheck,   href: "/dashboard/deliveries", roles: ["owner", "manager", "staff"] },
      { label: "Transfers",     icon: ArrowRightLeft, href: "/dashboard/transfers",  roles: ["owner", "manager"] },
      { label: "Checklists",    icon: CheckSquare,    href: "/dashboard/tasks",      roles: ["owner", "manager", "staff"] },
    ],
  },
  {
    label: "Tracking",
    items: [
      { label: "History",       icon: History,            href: "/dashboard/count-history",    roles: ["owner", "manager", "staff", "viewer"] },
      { label: "Waste Log",     icon: Trash2,           href: "/dashboard/waste",            roles: ["owner", "manager"] },
      { label: "Recipes",       icon: UtensilsCrossed,  href: "/dashboard/recipes",          roles: ["owner", "manager"] },
      { label: "Analytics",     icon: BarChart2,        href: "/dashboard/analytics",        roles: ["owner", "manager"] },
      { label: "Store Health",  icon: HeartPulse,       href: "/dashboard/store-comparison", roles: ["owner"] },
    ],
  },
];

const SETTINGS_ITEMS: NavItem[] = [
  { label: "Stores",       icon: Store,      href: "/dashboard/stores",       roles: ["owner", "manager"] },
  { label: "Team",         icon: Users,       href: "/dashboard/team",         roles: ["owner"] },
  { label: "Subscription", icon: CreditCard,  href: "/dashboard/subscription", roles: ["owner"] },
  { label: "Org Settings", icon: Settings,    href: "/dashboard/settings",     roles: ["owner", "manager"] },
  { label: "Profile",      icon: UserCircle,  href: "/dashboard/profile",      roles: ["owner", "manager", "staff", "viewer"] },
];

// Flat list for header title resolution
const ALL_NAV_ITEMS = [...TOP_LEVEL_ITEMS, ...NAV_GROUPS.flatMap((g) => g.items), ...SETTINGS_ITEMS];

function filterItems(items: NavItem[], role: AppRole | null) {
  if (!role) return items;
  return items.filter((item) => item.roles.includes(role));
}

export default function DashboardLayout({ children }: { children: ReactNode }) {
  const densityState = useCardDensity();
  const { signOut, user, subscription } = useAuth();
  const { organization, role, allOrgs, activeOrgId, setActiveOrgId } = useOrganization();
  const [orgSwitcherOpen, setOrgSwitcherOpen] = useState(false);
  const {
    selectedStoreId,
    setSelectedStoreId,
    previewRole,
    setPreviewRole,
    effectiveRole,
    availableStores,
  } = useStoreContext();
  const { theme, toggleTheme } = useTheme();
  const { isDualRole } = useDualRole();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [trialBannerDismissed, setTrialBannerDismissed] = useState(false);
  const { showWarning, dismissWarning } = useInactivityTimer(signOut);

  // Compute trial days remaining
  const trialDaysLeft = subscription.onTrial && subscription.trialEnd
    ? Math.max(0, differenceInDays(parseISO(subscription.trialEnd), new Date()))
    : null;

   const isActualOwner = role === "owner";
   const currentLabel = ALL_NAV_ITEMS.find((i) => i.href === location.pathname)?.label ?? "Dashboard";

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      {/* Preview mode banner */}
      {previewRole && (
        <div className="fixed inset-x-0 top-0 z-[60] flex items-center justify-center gap-3 bg-warning px-4 py-1.5 text-warning-foreground text-sm font-medium">
          <Eye className="h-4 w-4" />
          Viewing as {previewRole.charAt(0).toUpperCase() + previewRole.slice(1)}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 gap-1 px-2 text-xs text-warning-foreground hover:bg-warning/80"
            onClick={() => setPreviewRole(null)}
          >
            <EyeOff className="h-3 w-3" /> Exit Preview
          </Button>
        </div>
      )}

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-foreground/20 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-sidebar text-sidebar-foreground transition-transform lg:static lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
          previewRole && "top-9"
        )}
      >
        <div className="flex h-16 items-center gap-3 border-b border-sidebar-border px-5">
          <div className="flex items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-accent/10 p-1.5">
            <img src={vrgLogo} alt="VRG Logo" className="h-7 w-7 rounded-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-sm font-bold leading-tight tracking-tight">Villa Real Group</p>
            {allOrgs.length > 1 ? (
              <Popover open={orgSwitcherOpen} onOpenChange={setOrgSwitcherOpen}>
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-0.5 text-[11px] text-sidebar-foreground/50 hover:text-sidebar-foreground/80 transition-colors truncate max-w-full mt-0.5">
                    <span className="truncate">{organization?.name ?? "Loading…"}</span>
                    <ChevronDown className="h-2.5 w-2.5 shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-52 p-1">
                  {allOrgs.map((m) => (
                    <button
                      key={m.organization.id}
                      onClick={() => {
                        setActiveOrgId(m.organization.id);
                        setOrgSwitcherOpen(false);
                      }}
                      className={cn(
                        "w-full rounded-md px-3 py-2 text-left text-sm transition-colors hover:bg-accent",
                        m.organization.id === activeOrgId && "bg-accent font-medium"
                      )}
                    >
                      {m.organization.name}
                      <span className="ml-1.5 text-xs text-muted-foreground capitalize">{m.role}</span>
                    </button>
                  ))}
                </PopoverContent>
              </Popover>
            ) : (
              <p className="text-[11px] text-sidebar-foreground/50 truncate">{organization?.name ?? "Loading…"}</p>
            )}
          </div>
          <button className="lg:hidden min-h-[44px] min-w-[44px] flex items-center justify-center" onClick={() => setSidebarOpen(false)}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Trial banner */}
        {trialDaysLeft !== null && !trialBannerDismissed && (
          <div className="mx-3 mt-3 flex items-center gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs">
            <Clock className="h-3.5 w-3.5 shrink-0 text-warning" />
            <span className="flex-1 font-medium text-warning-foreground">
              Trial ends in {trialDaysLeft} day{trialDaysLeft !== 1 ? "s" : ""}
            </span>
            <button onClick={() => setTrialBannerDismissed(true)} className="text-warning-foreground/60 hover:text-warning-foreground">
              <X className="h-3 w-3" />
            </button>
          </div>
        )}

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          {/* Top-level items */}
          {filterItems(TOP_LEVEL_ITEMS, effectiveRole).map((item) => (
            <NavLink
              key={item.href}
              item={item}
              active={location.pathname === item.href}
              onClick={() => setSidebarOpen(false)}
            />
          ))}

          {/* Grouped sections */}
          {NAV_GROUPS.map((group) => {
            const visibleItems = filterItems(group.items, effectiveRole);
            if (visibleItems.length === 0) return null;
            return (
              <CollapsibleNavGroup
                key={group.label}
                label={group.label}
                items={visibleItems}
                currentPath={location.pathname}
                onNavigate={() => setSidebarOpen(false)}
              />
            );
          })}

          {/* Settings section */}
          {(() => {
            const visibleSettings = filterItems(SETTINGS_ITEMS, effectiveRole);
            if (visibleSettings.length === 0) return null;
            return (
              <div className="mt-auto pt-3 border-t border-sidebar-border/50">
                <div className="flex items-center gap-2 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-sidebar-foreground/40">
                  <Settings className="h-3 w-3" />
                  Settings
                </div>
                <div className="space-y-0.5">
                  {visibleSettings.map((item) => (
                    <NavLink
                      key={item.href}
                      item={item}
                      active={location.pathname === item.href}
                      onClick={() => setSidebarOpen(false)}
                    />
                  ))}
                </div>
              </div>
            );
          })()}
        </nav>

        <div className="border-t border-sidebar-border p-4 space-y-2">
          {/* Role preview toggle (owner only) */}
          {isActualOwner && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "w-full justify-start gap-2 text-xs",
                    previewRole
                      ? "text-warning"
                      : "text-sidebar-foreground/50 hover:text-sidebar-foreground"
                  )}
                >
                  <Eye className="h-3.5 w-3.5" />
                  {previewRole ? `Previewing: ${previewRole}` : "Preview as…"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-1" side="top" align="start">
                <button
                  className={cn("flex w-full items-center rounded-md px-3 py-2 text-sm hover:bg-accent", previewRole === "manager" && "bg-accent")}
                  onClick={() => setPreviewRole(previewRole === "manager" ? null : "manager")}
                >
                  View as Manager
                </button>
                <button
                  className={cn("flex w-full items-center rounded-md px-3 py-2 text-sm hover:bg-accent", previewRole === "staff" && "bg-accent")}
                  onClick={() => setPreviewRole(previewRole === "staff" ? null : "staff")}
                >
                  View as Staff
                </button>
                <button
                  className={cn("flex w-full items-center rounded-md px-3 py-2 text-sm hover:bg-accent", previewRole === "viewer" && "bg-accent")}
                  onClick={() => setPreviewRole(previewRole === "viewer" ? null : "viewer")}
                >
                  View as Viewer
                </button>
                {previewRole && (
                  <>
                    <div className="my-1 border-t" />
                    <button
                      className="flex w-full items-center rounded-md px-3 py-2 text-sm text-destructive hover:bg-accent"
                      onClick={() => setPreviewRole(null)}
                    >
                      Exit Preview
                    </button>
                  </>
                )}
              </PopoverContent>
            </Popover>
          )}

          {/* Dark mode toggle */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 text-xs text-sidebar-foreground/50 hover:text-sidebar-foreground"
            onClick={toggleTheme}
          >
            {theme === "dark" ? <Sun className="h-3.5 w-3.5" /> : <Moon className="h-3.5 w-3.5" />}
            {theme === "dark" ? "Light Mode" : "Dark Mode"}
          </Button>

          {isDualRole && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2 text-xs text-sidebar-foreground/50 hover:text-sidebar-foreground border border-sidebar-border/40 hover:border-violet-500/40"
              onClick={() => { setPortalChoice("franchise"); navigate("/franchise-portal"); }}
            >
              <Building2 className="h-3.5 w-3.5 text-violet-400" />
              Switch to Franchise Portal
            </Button>
          )}

          <div className="flex items-center gap-2.5 pt-1">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-primary/30 to-accent/20 text-xs font-bold uppercase text-sidebar-foreground">
              {user?.email?.[0] ?? "?"}
            </div>
            <div className="flex-1 truncate">
              <p className="truncate text-xs font-medium">{user?.email}</p>
              <p className="text-[11px] capitalize text-sidebar-foreground/50">{effectiveRole ?? ""}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start gap-2 text-sidebar-foreground/70 hover:text-sidebar-foreground"
            onClick={signOut}
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className={cn("flex flex-1 flex-col overflow-hidden bg-background", previewRole && "pt-9")}>
        <header className="flex h-14 items-center gap-2 bg-background shadow-sm px-3 lg:h-16 lg:gap-4 lg:px-6">
          <button className="lg:hidden min-h-[44px] min-w-[44px] flex items-center justify-center" onClick={() => setSidebarOpen(true)}>
            <MenuIcon className="h-5 w-5" />
          </button>
          <h2 className="font-display text-base font-semibold tracking-tight lg:text-lg truncate">{currentLabel}</h2>
          <div className="flex-1" />
          <div className="ml-auto flex items-center gap-1.5 lg:gap-2">
            <NotificationBell />
            {availableStores.length > 0 && (
              <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
                <SelectTrigger className="w-28 h-10 min-h-[44px] text-sm rounded-full bg-muted border-none sm:w-36 lg:w-44">
                  <Store className="mr-1 h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <SelectValue placeholder="Store" />
                </SelectTrigger>
                <SelectContent>
                  {(isActualOwner || previewRole === null) && effectiveRole !== "manager" && effectiveRole !== "staff" && (
                    <SelectItem value="all">All Stores</SelectItem>
                  )}
                  {availableStores.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </header>
        <main
          className="flex-1 overflow-y-auto pb-4 px-4 lg:pb-6 lg:px-6"
          style={{ paddingBottom: `calc(1.5rem + env(safe-area-inset-bottom))` }}
        >
          <DensityContext.Provider value={densityState}>
            {children}
          </DensityContext.Provider>
        </main>
      </div>
      <InactivityWarning open={showWarning} onStay={dismissWarning} onLogout={signOut} />
    </div>
  );
}

// ── NavLink ──────────────────────────────────────────────────────────────────
function NavLink({ item, active, onClick }: { item: NavItem; active: boolean; onClick: () => void }) {
  return (
    <Link
      to={item.href}
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200 min-h-[44px]",
        active
          ? "bg-sidebar-primary/15 text-sidebar-primary shadow-sm"
          : "text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-foreground"
      )}
    >
      <item.icon className={cn("h-4 w-4", active && "text-sidebar-primary")} />
      {item.label}
      {active && <ChevronRight className="ml-auto h-3.5 w-3.5 text-sidebar-primary/60" />}
    </Link>
  );
}

// ── Collapsible Nav Group ────────────────────────────────────────────────────
function CollapsibleNavGroup({
  label,
  items,
  currentPath,
  onNavigate,
}: {
  label: string;
  items: NavItem[];
  currentPath: string;
  onNavigate: () => void;
}) {
  const hasActiveChild = items.some((i) => i.href === currentPath);
  const [open, setOpen] = useState(true);

  // Auto-expand when route changes into this group
  useEffect(() => {
    if (hasActiveChild) setOpen(true);
  }, [hasActiveChild]);

  return (
    <div className="pt-2">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-sidebar-foreground/40 hover:text-sidebar-foreground/60 transition-colors min-h-[44px]"
      >
        <ChevronDown
          className={cn(
            "h-3 w-3 transition-transform duration-200",
            !open && "-rotate-90"
          )}
        />
        {label}
      </button>
      {open && (
        <div className="mt-0.5 space-y-0.5">
          {items.map((item) => (
            <NavLink
              key={item.href}
              item={item}
              active={currentPath === item.href}
              onClick={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}